// Desk Dashboard: the routes, the sidebar row and the core of the page of
// `wealth.portfolio.desk-dashboard` (ADR-0027). The core is the book itself:
// the client, household and mandate figures of the tape, the risk split, the
// top holdings and the client table. The other figures and cards come from
// the feature that owns them.
import { lazy } from 'react';
import { Navigate } from 'react-router';
import type { StatStripItem } from '@/components/ui/StatStrip';
import { defineWebFeature } from '@/features/kit';
import { ABSENT, money } from '@/lib/overview-figures';
import type { DeskDashboardCtx, ServiceFilterKey } from '@/pages/desk/my-book/slots';

const RiskSplitCard = lazy(() =>
    import('./desk-dashboard.slots').then((m) => ({ default: m.RiskSplitCard })),
);
const TopHoldingsCard = lazy(() =>
    import('./desk-dashboard.slots').then((m) => ({ default: m.TopHoldingsCard })),
);
const YourClientsCard = lazy(() =>
    import('./desk-dashboard.slots').then((m) => ({ default: m.YourClientsCard })),
);

/** A figure of the tape that sets the mandate filter. */
function filterItem(
    ctx: DeskDashboardCtx,
    key: ServiceFilterKey,
    item: Omit<StatStripItem, 'onClick' | 'active' | 'to'>,
): StatStripItem {
    return {
        ...item,
        active: ctx.serviceFilter === key && key !== 'all',
        onClick: () => ctx.setServiceFilter(key),
    };
}

export default defineWebFeature({
    id: 'wealth.portfolio.desk-dashboard',
    routes: [
        { frontend: 'desk', path: '', lazy: () => import('@/pages/desk/page') },
        { frontend: 'desk', path: 'dashboard', lazy: () => import('@/pages/desk/my-book/page') },
        { frontend: 'desk', path: 'my-book', lazy: () => import('@/pages/desk/my-book/page') },
        // My Page is retired (SEP-299). This route sends a saved link to
        // /desk/my-page to the Desk Dashboard for one release. Remove it in the
        // next release.
        { frontend: 'desk', path: 'my-page', element: <Navigate to="/desk/dashboard" replace /> },
    ],
    nav: [
        // --- Overview ---
        {
            frontend: 'desk',
            key: 'my-book',
            label: 'Desk Dashboard',
            href: '/desk/dashboard',
            icon: 'LayoutDashboard',
            group: 'overview',
            order: 110,
            aliases: ['/desk/my-book'],
        },
    ],
    slots: {
        'desk.dashboard.tape': [
            {
                key: 'clients',
                order: 10,
                select: (_input, ctx): StatStripItem => ({
                    key: 'clients',
                    label: 'Clients',
                    value: ctx.dashboard ? ctx.stats.clientCount : ABSENT,
                    hint: 'Clients in view. Opens the Client Book.',
                    to: '/desk/client-book',
                    testId: 'stat-clients',
                }),
            },
            {
                key: 'family-groups',
                order: 20,
                select: (_input, ctx): StatStripItem => ({
                    key: 'family-groups',
                    label: 'Family groups',
                    value: ctx.dashboard ? ctx.stats.householdCount : ABSENT,
                    hint: `Households of the clients in view. ${ctx.stats.ungroupedCount} clients are in no family group. Opens the Client Book.`,
                    to: '/desk/client-book',
                    testId: 'stat-family-groups',
                }),
            },
            {
                key: 'aum',
                order: 30,
                select: (_input, ctx) =>
                    filterItem(ctx, 'discretionary', {
                        key: 'aum',
                        label: 'AUM',
                        value: money(ctx.dashboard?.stats.totalAumGbp),
                        hint: [
                            'Discretionary mandate. Press to show these clients only.',
                            `AUA Advisory ${money(ctx.dashboard?.stats.totalAdvisoryGbp)}`,
                            `AUA XO ${money(ctx.dashboard?.stats.totalXoGbp)}`,
                            `Book total ${money(ctx.dashboard?.stats.grandTotalGbp)}`,
                        ].join(' · '),
                        testId: 'stat-aum',
                    }),
            },
        ],
        'desk.dashboard.cards': [
            {
                key: 'risk-split',
                order: 60,
                span: 4,
                // Four rows hold the five risk profiles, and they give the card
                // the height of the peer reviews card beside it.
                rows: 4,
                title: 'Book by risk profile',
                summary: 'The value of the clients in view, by risk profile.',
                emptyBehaviour: 'keep',
                Component: RiskSplitCard,
            },
            {
                key: 'top-holdings',
                order: 70,
                span: 8,
                rows: 'fit',
                title: 'Top holdings',
                summary: 'The largest holdings of the discretionary book, by value.',
                emptyBehaviour: 'keep',
                fitRows: (ctx) => ctx.overview?.topHoldings.length ?? 0,
                Component: TopHoldingsCard,
            },
            {
                key: 'your-clients',
                order: 90,
                span: 12,
                rows: 'fit',
                title: 'Your clients',
                summary: 'The clients in view, with their flags.',
                emptyBehaviour: 'keep',
                // A card stops at 12 grid rows, which hold about 25 table rows.
                // The body of the card scrolls the other rows. The table has
                // taller rows than a tight table and a pager below the rows.
                // These take about two table rows more.
                fitRows: (ctx) => Math.min(ctx.clients.length, 25) + 2,
                Component: YourClientsCard,
            },
        ],
    },
});
